import React, { useState, useEffect, useCallback } from 'react';
import { colors, radius, fonts } from '../theme';
import { auth } from '../api';
import FloatingParticles from '../components/FloatingParticles';

export function useAuth() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('client_token');
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const res = await auth.me();
      setUser(res.data);
      localStorage.setItem('client_user', JSON.stringify(res.data));
    } catch {
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_user');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email: string, password: string) => {
    const res = await auth.login(email, password);
    localStorage.setItem('client_token', res.data.access_token);
    setUser(res.data.user);
    localStorage.setItem('client_user', JSON.stringify(res.data.user));
  };

  const register = async (data: any) => {
    const res = await auth.register(data);
    localStorage.setItem('client_token', res.data.access_token);
    setUser(res.data.user);
    localStorage.setItem('client_user', JSON.stringify(res.data.user));
  };

  const logout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    setUser(null);
  };

  return { user, loading, login, register, logout, refreshUser: loadUser };
}

// --- Login Page ---
export function LoginPage({ onLogin }: { onLogin: (email: string, password: string) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await onLogin(email, password);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, #000, #140b08, #1A1A3A)', position: 'relative', overflow: 'hidden' }}>
      {/* Subtle ambient glow */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at top, rgba(255,180,60,0.08), transparent 50%)', pointerEvents: 'none' }} />
      {/* Floating particles for ambiance */}
      <FloatingParticles count={12} />
      <div style={{ background: 'rgba(26,26,58,0.92)', borderRadius: radius.lg, padding: 40, width: 380, border: `1px solid ${colors.border}`, position: 'relative', backdropFilter: 'blur(4px)', boxShadow: '0 0 60px rgba(201,169,110,0.1)' }}>
        <div style={{ width: 120, height: 120, borderRadius: '50%', overflow: 'hidden', margin: '0 auto 16px', border: '3px solid #C9A96E', boxShadow: '0 0 20px rgba(201,169,110,0.5), 0 0 50px rgba(201,169,110,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src="/buddha_v3_login_glow_256.png" alt="Le Siam" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <h1 style={{ color: colors.gold, fontSize: 36, fontWeight: 'bold', textAlign: 'center', margin: 0 }}>Le Siam</h1>
        <p style={{ color: colors.textMuted, textAlign: 'center', marginBottom: 32, fontSize: 14 }}>Bar Premium &bull; Traiteur Thaï &bull; Cave à Vins</p>
        {error && <div style={{ color: colors.error, background: `${colors.error}15`, padding: 12, borderRadius: radius.sm, marginBottom: 16, fontSize: 14 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <input placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
          <input placeholder="Mot de passe" type="password" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} />
          <button type="submit" disabled={busy} style={btnPrimary}>{busy ? 'Connexion...' : 'Se connecter'}</button>
        </form>
        <p style={{ color: colors.textMuted, textAlign: 'center', marginTop: 20, fontSize: 13 }}>
          Pas encore inscrit ? <a href="#/register" style={{ color: colors.gold, textDecoration: 'none' }}>Créer un compte</a>
        </p>
      </div>
    </div>
  );
}

// --- Register Page ---
export function RegisterPage({ onRegister }: { onRegister: (data: any) => Promise<void> }) {
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', phone: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await onRegister(form);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur d\'inscription');
    }
    setBusy(false);
  };

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, #000, #140b08, #1A1A3A)', position: 'relative', overflow: 'hidden' }}>
      {/* Subtle ambient glow */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at top, rgba(255,180,60,0.08), transparent 50%)', pointerEvents: 'none' }} />
      <FloatingParticles count={12} />
      <div style={{ background: 'rgba(26,26,58,0.92)', borderRadius: radius.lg, padding: 40, width: 420, border: `1px solid ${colors.border}`, position: 'relative', backdropFilter: 'blur(4px)', boxShadow: '0 0 60px rgba(201,169,110,0.1)' }}>
        <div style={{ width: 120, height: 120, borderRadius: '50%', overflow: 'hidden', margin: '0 auto 16px', border: '3px solid #C9A96E', boxShadow: '0 0 20px rgba(201,169,110,0.5), 0 0 50px rgba(201,169,110,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src="/buddha_v3_login_glow_256.png" alt="Le Siam" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <h1 style={{ color: colors.gold, fontSize: 32, fontWeight: 'bold', textAlign: 'center', margin: 0 }}>Créer un compte</h1>
        <p style={{ color: colors.textMuted, textAlign: 'center', marginBottom: 32, fontSize: 14 }}>Rejoignez Le Siam</p>
        {error && <div style={{ color: colors.error, background: `${colors.error}15`, padding: 12, borderRadius: radius.sm, marginBottom: 16, fontSize: 14 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <input placeholder="Email" type="email" value={form.email} onChange={set('email')} required style={inputStyle} />
          <input placeholder="Mot de passe" type="password" value={form.password} onChange={set('password')} required style={inputStyle} />
          <div style={{ display: 'flex', gap: 12 }}>
            <input placeholder="Prénom" value={form.first_name} onChange={set('first_name')} style={{ ...inputStyle, flex: 1 }} />
            <input placeholder="Nom" value={form.last_name} onChange={set('last_name')} style={{ ...inputStyle, flex: 1 }} />
          </div>
          <input placeholder="Téléphone" value={form.phone} onChange={set('phone')} style={inputStyle} />
          <button type="submit" disabled={busy} style={btnPrimary}>{busy ? 'Inscription...' : 'S\'inscrire'}</button>
        </form>
        <p style={{ color: colors.textMuted, textAlign: 'center', marginTop: 20, fontSize: 13 }}>
          Déjà inscrit ? <a href="#/login" style={{ color: colors.gold, textDecoration: 'none' }}>Se connecter</a>
        </p>
      </div>
    </div>
  );
}

// --- Profile Page ---
export function ProfilePage({ user, onRefresh }: { user: any, onRefresh: () => void }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({
      first_name: user.first_name || '', last_name: user.last_name || '',
      phone: user.phone || '', date_of_birth: user.date_of_birth || '',
      zone_of_residence: user.zone_of_residence || '',
      allergies: user.allergies || '', interests: user.interests || '',
    });
  }, [user]);

  const handleSave = async () => {
    setBusy(true);
    try {
      await auth.updateMe(form);
      setMsg('Profil mis à jour');
      setEditing(false);
      onRefresh();
    } catch { setMsg('Erreur'); }
    setBusy(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    try {
      await auth.uploadPhoto(e.target.files[0]);
      onRefresh();
    } catch {}
  };

  if (!user) return null;

  const fields = [
    ['Prénom', 'first_name'], ['Nom', 'last_name'], ['Téléphone', 'phone'],
    ['Date de naissance', 'date_of_birth', 'date'], ['Zone', 'zone_of_residence'],
    ['Allergies', 'allergies'], ['Centres d\'intérêt', 'interests'],
  ];

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <h2 style={{ color: colors.gold, fontSize: 24, marginBottom: 24 }}>Mon Profil</h2>
      <div style={{ background: colors.card, borderRadius: radius.md, padding: 24, border: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: colors.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 'bold', color: colors.bg, overflow: 'hidden' }}>
            {user.photo_url ? <img src={user.photo_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : (user.first_name?.[0] || '?').toUpperCase()}
          </div>
          <div>
            <div style={{ color: colors.text, fontSize: 20, fontWeight: 'bold' }}>{user.first_name} {user.last_name}</div>
            <div style={{ color: colors.textMuted, fontSize: 13 }}>{user.email}</div>
            <div style={{ color: colors.gold, fontSize: 13, marginTop: 4 }}>{user.loyalty_points} points fidélité</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <label style={{ ...btnSecondary, cursor: 'pointer', fontSize: 12 }}>
              Photo
              <input type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
            </label>
          </div>
        </div>
        {msg && <div style={{ color: colors.success, marginBottom: 12, fontSize: 14 }}>{msg}</div>}
        {fields.map(([label, key, type]: any) => (
          <div key={key} style={{ marginBottom: 12 }}>
            <label style={{ color: colors.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, display: 'block' }}>{label}</label>
            {editing ? (
              <input value={form[key] || ''} type={type || 'text'} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
            ) : (
              <div style={{ color: colors.text, fontSize: 15, padding: '8px 0' }}>{user[key as any] || '—'}</div>
            )}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          {editing ? (
            <>
              <button onClick={handleSave} disabled={busy} style={btnPrimary}>{busy ? '...' : 'Enregistrer'}</button>
              <button onClick={() => setEditing(false)} style={btnSecondary}>Annuler</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} style={btnPrimary}>Modifier</button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Shared styles ---
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 16px', background: colors.inputBg,
  border: `1px solid ${colors.border}`, borderRadius: radius.sm,
  color: colors.text, fontSize: 14, marginBottom: 12, outline: 'none',
  fontFamily: fonts.body, boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  width: '100%', padding: '12px', background: colors.gold, color: colors.bg,
  border: 'none', borderRadius: radius.sm, fontSize: 15, fontWeight: 'bold',
  cursor: 'pointer', fontFamily: fonts.body,
};

const btnSecondary: React.CSSProperties = {
  padding: '10px 20px', background: 'transparent', color: colors.gold,
  border: `1px solid ${colors.gold}`, borderRadius: radius.sm, fontSize: 14,
  cursor: 'pointer', fontFamily: fonts.body,
};